import React from 'react';
import {type ColumnDef} from '@tanstack/react-table';
import {Plus, Search, Maximize2, Minimize2} from 'lucide-react';
import ColumnTitle from '@/components/ui/DataTable/ColumnTitle';
import {TimeFormatted} from '@/components/ui/Time/TimeFormatted';
import DataTable from "@/components/ui/DataTable";
import {PageToolbar} from "@/components/layouts/PageLayout/PageToolbar";
import {PageTitle} from "@/components/layouts/PageLayout/PageTitle";
import {useNavigate, useSearchParams} from "react-router-dom";
import {ROUTES} from "@/constants/routes.constants";
import {PriorityName} from "@/types/priority.types";
import {StatusName} from "@/types/status.types";
import StatusBadge from "@/components/ui/Badge/StatusBadge";
import PriorityBadge from "@/components/ui/Badge/PriorityBadge";
import {useGetTicketsQuery} from '@/components/features/tickets/services/tickets.queries';
import {useQueryClient} from '@tanstack/react-query';
import {TICKET_KEYS} from '@/components/features/tickets/services/tickets.queryKeys';
import type {Ticket, TicketFilters} from '@/types/ticket.types';
import useDebounce from "@/hooks/useDebounce";
import {ButtonVariant} from "@/components/ui/Button/ButtonSize";
import InputWithLabel from "@/components/ui/InputWithLabel";
import StatusDropdown from "@/components/ui/DropdownWithLabel/StatusDropdown";
import PriorityDropdown from "@/components/ui/DropdownWithLabel/PriorityDropdown";
import CategoryDropdown from "@/components/ui/DropdownWithLabel/CategoryDropdown";
import SectionDropdown from "@/components/ui/DropdownWithLabel/SectionDropdown";
import Button from "@/components/ui/Button";
import useGlobalStore from "@/stores/useGlobalStore";
import DepartmentDropdown from "@/components/ui/DropdownWithLabel/DepartmentDropdown";
import ProductDropdown from "@/components/ui/DropdownWithLabel/ProductDropdown";
import {isClient, isMindsysExternalPartner} from "@/utils/auth.utils";
import useUserStore from "@/stores/useUserStore";
import MonthDropdown from "@/components/ui/DropdownWithLabel/MonthDropdown";
import YearDropdown from "@/components/ui/DropdownWithLabel/YearDropdown";
import CompanyDropdown from "@/components/ui/DropdownWithLabel/CompanyDropdown";
import AssigneeDropdown from "@/components/ui/DropdownWithLabel/AssigneeDropdown";
import ActiveFilters from "@/components/ui/ActiveFilters/ActiveFilters";
import {Tooltip} from "@/components/ui/Tooltip/Tooltip";

interface TicketRow {
    id: string;
    number: string;
    title: string;
    status: StatusName;
    priority: PriorityName;
    company: string;
    department: string;
    assignee?: string;
    createdAt: string;
    updatedAt: string;
    category: string;
    caseNumbers: string;
}

const mapTicketToRow = (ticket: Ticket): TicketRow => ({
    id: ticket.ticket_id,
    number: ticket.ticket_number.toString(),
    title: ticket.title,
    status: ticket.status.status_name as StatusName,
    priority: ticket.priority.priority_name as PriorityName,
    company: ticket.company.company_name,
    department: ticket.department.department_name,
    assignee: ticket.assignee ? `${ticket.assignee.first_name} ${ticket.assignee.last_name}` : undefined,
    category: ticket.category.category_name,
    caseNumbers: ticket.case_numbers,
    createdAt: ticket.created_at,
    updatedAt: ticket.updated_at
});

const columns: ColumnDef<TicketRow, string>[] = [
    {
        id: 'number',
        header: ({column}) => (
            <ColumnTitle title="Αίτημα" isSortable column={column}/>
        ),
        accessorFn: (row) => row.number,
        cell: ({row}) => (
            <div className="flex flex-col gap-1.5">
                <div className="flex items-center gap-2">
                    <span className="font-medium text-gray-900">#{row.original.number}</span>
                    <div className="flex items-center gap-1">
                        <div className="scale-85">
                            <StatusBadge status={row.original.status}/>
                        </div>
                        <div className="scale-85">
                            <PriorityBadge priority={row.original.priority}/>
                        </div>
                    </div>
                </div>
                <Tooltip content={row.original.title}>
                    <span className="text-sm text-gray-500 truncate max-w-[300px] block cursor-help">
                        {row.original.title}
                    </span>
                </Tooltip>
            </div>
        ),
    },
    // {
    //     id: 'caseNumbers',
    //     header: ({column}) => (
    //         <ColumnTitle title="Υποθέσεις" isSortable column={column}/>
    //     ),
    //     accessorFn: (row) => row.caseNumbers,
    //     cell: ({row}) => (
    //         <div className="flex flex-col">
    //             <span className="font-medium text-gray-900">{row.original.caseNumbers}</span>
    //         </div>
    //     ),
    // },
    {
        id: 'assignee',
        header: ({column}) => (
            <ColumnTitle title="Ανατέθηκε" isSortable column={column}/>
        ),
        accessorKey: 'assignee',
        cell: ({row}) => (
            row.original.assignee ||
            <span className="text-gray-400 italic">Μη ανατεθειμένο</span>
        ),
    },
    {
        id: 'companyDepartment',
        header: ({column}) => (
            <ColumnTitle title="Εταιρεία / Τμήμα" isSortable column={column}/>
        ),
        accessorKey: 'company',
        cell: ({row}) => (
            <div className="flex flex-col">
                <span className="font-medium text-gray-900">{row.original.company}</span>
                <span className="text-xs text-gray-500">{row.original.department}</span>
            </div>
        ),
    },
    {
        id: 'category',
        header: ({column}) => (
            <ColumnTitle title="Κατηγορία" isSortable column={column}/>
        ),
        accessorKey: 'category',
    },
    {
        id: 'updatedAt',
        header: ({column}) => (
            <ColumnTitle title="Τελευταία Ενημέρωση" isSortable column={column}/>
        ),
        accessorFn: (row) => new Date(row.updatedAt).getTime().toString(),
        cell: ({row}) => (
            <TimeFormatted date={row.original.updatedAt}/>
        ),
    },
];


const FiltersPanel: React.FC<{
    filters: TicketFilters;
    onFiltersChange: (filters: TicketFilters) => void;
    onSearchChange: (search: string) => void;
    searchTerm: string;
    onClear: () => void;
}> = ({filters, onFiltersChange, onSearchChange, searchTerm, onClear}) => {
    const {user} = useUserStore();
    const roles = user?.roles || [];
    const isSupervisor = roles.includes('supervisor');

    const handleFilterChange = (key: keyof TicketFilters, value: string) => {
        const newFilters: TicketFilters = {
            ...filters,
            [key]: value || undefined,
            page_number: 1
        };
        
        // If company changes, clear department filter since departments are company-specific
        if (key === 'company_id') {
            newFilters.department_id = undefined;
        }
        
        // If section changes, clear category filter since categories are section-specific
        if (key === 'section_id') {
            newFilters.category_id = undefined;
        }
        
        onFiltersChange(newFilters);
    };

    return (
        <div className="bg-white p-4 rounded-lg shadow space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                {(!isClient(roles) || !isMindsysExternalPartner(roles)) && (
                    <>
                        <CompanyDropdown
                            value={filters.company_id ?? ''}
                            onChange={(value) => handleFilterChange('company_id', value)}
                            placeholder="Όλες οι εταιρίες"
                        />
                        <DepartmentDropdown
                            value={filters.department_id ?? ''}
                            onChange={(value) => handleFilterChange('department_id', value)}
                            placeholder="Όλα τα τμήματα"
                            company_id={filters.company_id}
                        />
                        {!isSupervisor && (
                            <ProductDropdown
                                value={filters.product_id ?? ''}
                                onChange={(value) => handleFilterChange('product_id', value)}
                                placeholder="Όλα τα προϊόντα"
                            />
                        )}
                        <MonthDropdown
                            value={filters.month ?? -1}
                            onChange={(value) => handleFilterChange('month', value.toString())}
                            placeholder="Όλοι οι Μήνες"
                        />
                        <YearDropdown
                            value={filters.year ?? -1}
                            onChange={(value) => handleFilterChange('year', value.toString())}
                            placeholder="Όλα τα Έτη"
                        />
                    </>
                )}

                <StatusDropdown
                    value={Array.isArray(filters.status_id) ? filters.status_id[0] : filters.status_id ?? ''}
                    onChange={(value) => handleFilterChange('status_id', value)}
                    placeholder="Όλες οι καταστάσεις"
                />
                <PriorityDropdown
                    value={filters.priority_id ?? ''}
                    onChange={(value) => handleFilterChange('priority_id', value)}
                    placeholder="Όλες οι προτεραιότητες"
                />
                <SectionDropdown
                    value={filters.section_id ?? ''}
                    onChange={(value: string) => handleFilterChange('section_id', value)}
                    placeholder="Όλες οι ενότητες"
                />
                <CategoryDropdown
                    value={filters.category_id ?? ''}
                    onChange={(value) => handleFilterChange('category_id', value)}
                    placeholder="Όλες οι κατηγορίες"
                />
                {!isSupervisor && (
                    <AssigneeDropdown
                        value={filters.user_id ?? ''}
                        onChange={(value) => handleFilterChange('user_id', value)}
                        placeholder="Όλοι οι υπεύθυνοι"
                    />
                )}
            </div>
            <div>
                <InputWithLabel
                    label="Αναζήτηση"
                    placeholder="Αναζήτηση με αριθμό αιτήματος ή τίτλο/περιγραφή..."
                    value={searchTerm}
                    onChange={(e) => onSearchChange(e.target.value)}
                    icon={Search}
                />
            </div>
            <div className="flex justify-end space-x-2">
                <Button
                    variant={ButtonVariant.OUTLINE}
                    onClick={onClear}
                >
                    Καθαρισμός
                </Button>
            </div>
        </div>
    );
};

const TicketsList = () => {
    // Status IDs for each group
    const STATUS_IDS = {
        new: [
            'f4603a0e-9d60-47b0-9c80-586c818174ea', // Νέο
            '2a08b77d-a1c9-44f1-a4c0-bfcf3ec9a0f5'  // Επαν-ανοίχτηκε
        ],
        in_progress: [
            'e528f9a5-dad7-4324-969a-6ced200a110d', // Ανοιχτό
            '2a08b77d-a1c9-44f1-a4c0-bfcf3ec9a0f5', // Επαν-ανοίχτηκε
            'e178224c-c910-45c7-9054-ec7bf92ec778'  // Σε Εκκρεμότητα
        ],
        completed: [
            '23a27a15-f755-4959-b6fc-59688ed1fecd', // Επιλυμένο
            '030e67f5-f4ea-4d75-9fd6-a3d46ce7ad81', // Κλειστό
            '64708027-ceda-4b5f-9100-7ac38023fd81'  // Ακυρωμένο
        ]
    };

    const [isFiltersOpen, setIsFiltersOpen] = React.useState(false);
    const [isTableFixed, setIsTableFixed] = React.useState(false);
    const { tableStates, setTableState, clearTableState } = useGlobalStore();
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const {user} = useUserStore();
    const roles = user?.roles || [];
    const [searchParams] = useSearchParams();

    // Get status group from URL parameter
    const statusGroup = searchParams.get('group') as 'new' | 'in_progress' | 'completed' | null;

    // Get stored state for this specific table
    // Use different table state keys for group pages vs all tickets
    const tableStateKey = statusGroup ? `tickets-table-${statusGroup}` : 'tickets-table';
    const tableState = tableStates[tableStateKey];

    const [filters, setFilters] = React.useState<TicketFilters>(() => ({
        page_number: (tableState?.pagination?.pageIndex ?? 0) + 1,
        page_size: tableState?.pagination?.pageSize ?? 50,
        ...tableState?.filters
    }));

    const [searchTerm, setSearchTerm] = React.useState('');
    const debouncedSearch = useDebounce(searchTerm, 500);

    // Clear filters and invalidate cache when navigating between different group pages
    const previousStatusGroup = React.useRef(statusGroup);
    React.useEffect(() => {
        // Only reset if the group actually changed (not on mount or other re-renders)
        if (previousStatusGroup.current !== statusGroup) {
            setFilters({
                page_number: 1,
                page_size: 50,
            });
            setSearchTerm('');
            
            // Invalidate queries to force refetch with new status group
            queryClient.invalidateQueries({
                queryKey: TICKET_KEYS.base
            });
            
            previousStatusGroup.current = statusGroup;
        }
    }, [statusGroup, queryClient]); // Reset filters when group changes

    // Build query with status_id array if group is specified
    const queryFilters: TicketFilters = {
        ...filters,
        search: debouncedSearch || undefined,
    };

    // If a status group is specified, add the array of status IDs
    if (statusGroup && STATUS_IDS[statusGroup]) {
        queryFilters.status_id = STATUS_IDS[statusGroup];
    }

    // Single query that handles both regular and grouped filtering
    const {data, isLoading, isError} = useGetTicketsQuery(queryFilters);

    const tickets = React.useMemo(() => {
        if (!data?.data) return [];
        return data.data.map(mapTicketToRow);
        }, [data]);

    const handleFiltersChange = (newFilters: TicketFilters) => {
        setFilters(newFilters);
    };

    const handleRemoveFilter = (key: keyof TicketFilters) => {
        setFilters({
            ...filters,
            [key]: undefined,
            page_number: 1
        });
    };

    const handleClearFilters = () => {
        clearTableState(tableStateKey);
        setFilters({
            page_number: 1,
            page_size: filters.page_size,
            ticket_number: undefined,
            ticket_text: undefined,
            status_id: undefined,
            priority_id: undefined,
            section_id: undefined,
            category_id: undefined,
            company_id: undefined,
            department_id: undefined,
            product_id: undefined,
            user_id: undefined,
            month: undefined,
            year: undefined
        });
        setSearchTerm('');


        // Force clear dropdown values
        const dropdowns = document.querySelectorAll('select');
        dropdowns.forEach(dropdown => {
            dropdown.value = '';
        });
    };

    const getActiveFiltersCount = () => {
        return Object.entries(filters).filter(([key, value]) =>
            key !== 'page_number' &&
            key !== 'page_size' &&
            key !== 'ticket_number' &&
            key !== 'ticket_text' &&
            value !== undefined &&
            value !== ''
        ).length + (searchTerm ? 1 : 0);
    };

    const handleAddNew = () => {
        navigate(ROUTES.TICKETS.NEW);
    };

    const handleRefresh = async () => {
        await queryClient.refetchQueries({
            queryKey: [TICKET_KEYS.base]
        });
    };

    const handleRowClick = (row: TicketRow) => {
        // Save current state using the correct table state key
        setTableState(tableStateKey, {
            filters: {
                ...filters,
                search: debouncedSearch || undefined
            },
            pagination: {
                pageIndex: (filters.page_number ?? 1) - 1,
                pageSize: filters.page_size ?? 10,
            }
        });

        navigate(ROUTES.TICKETS.DETAILS(row.id));
    };

    const handlePageChange = (pageIndex: number, pageSize: number) => {
        const newFilters = {
            ...filters,
            page_number: pageIndex + 1,
            page_size: pageSize
        };
        setFilters(newFilters);

        // Update stored state using the correct table state key
        setTableState(tableStateKey, {
            filters: newFilters,
            pagination: { pageIndex, pageSize }
        });
    };

    const visibleColumns = {
        caseNumbers: !isClient(roles)
    }

    const handleSearchChange = (searchValue: string) => {
        setSearchTerm(searchValue);

        // Check if search term is a number
        const isNumber = /^\d+$/.test(searchValue);

        setFilters(prev => ({
            ...prev,
            ticket_number: isNumber ? searchValue : undefined,
            ticket_text: isNumber ? undefined : searchValue || undefined,
            page_number: 1 // Reset to first page on search
        }));
    };

    React.useEffect(() => {
        // Get the stored state for this specific table
        const tableState = useGlobalStore.getState().tableStates['tickets-table'];

        if (tableState) {
            // Set filters from stored state
            setFilters({
                ...tableState.filters,
                page_number: (tableState.pagination?.pageIndex ?? 0) + 1,
                page_size: tableState.pagination?.pageSize ?? 10
            });

            // Set search term if it exists in stored filters
            if (tableState.filters) {
                setSearchTerm(
                    tableState.filters.ticket_number ||
                    tableState.filters.ticket_text ||
                    ''
                );
            }

            // Clear the stored state after applying it
            useGlobalStore.getState().clearTableState('tickets-table');
        }
    }, []);

    if (isError) {
        return (
            <div className="h-full w-full flex items-center justify-center">
                <p className="text-error-500">Σφάλμα φόρτωσης δεδομένων</p>
            </div>
        );
    }

    // Get page title based on status group
    const getPageTitle = () => {
        if (statusGroup === 'new') return 'Νέα Αιτήματα';
        if (statusGroup === 'in_progress') return 'Αιτήματα σε Εξέλιξη';
        if (statusGroup === 'completed') return 'Ολοκληρωμένα Αιτήματα';
        return 'Αιτήματα';
    };

    const getPageDescription = () => {
        if (statusGroup === 'new') return 'Προβολή νέων αιτημάτων';
        if (statusGroup === 'in_progress') return 'Προβολή αιτημάτων σε εξέλιξη (ανοιχτά, επανανοίχτηκαν, εκκρεμή)';
        if (statusGroup === 'completed') return 'Προβολή ολοκληρωμένων αιτημάτων (επιλύθηκε, ακυρώθηκε, κλειστά)';
        return 'Διαχείριση και παρακολούθηση όλων των αιτημάτων υποστήριξης';
    };

    return (
        <div className={`h-full w-full mx-auto p-4 sm:p-6 lg:p-8 ${isTableFixed ? 'flex flex-col overflow-hidden' : ''}`}>
            <div className={isTableFixed ? 'flex-shrink-0' : ''}>
                <PageTitle
                    title={getPageTitle()}
                    description={getPageDescription()}
                />
                <PageToolbar
                    primaryAction={{
                        label: 'Νέο Αίτημα',
                        icon: Plus,
                        onClick: handleAddNew
                    }}
                    onRefreshClick={handleRefresh}
                    onFilterClick={() => setIsFiltersOpen(!isFiltersOpen)}
                    activeFiltersCount={getActiveFiltersCount()}
                    secondaryActions={[
                        {
                            label: isTableFixed ? 'Απενεργοποίηση Σταθερής Προβολής' : 'Ενεργοποίηση Σταθερής Προβολής',
                            icon: isTableFixed ? Minimize2 : Maximize2,
                            onClick: () => setIsTableFixed(!isTableFixed),
                            variant: 'secondary' as ButtonVariant
                        }
                    ]}
                />
                {isFiltersOpen && (
                    <div className="mb-4">
                        <FiltersPanel
                            filters={filters}
                            onFiltersChange={handleFiltersChange}
                            searchTerm={searchTerm}
                            onSearchChange={handleSearchChange}
                            onClear={handleClearFilters}
                        />
                    </div>
                )}
                <ActiveFilters
                    filters={filters}
                    onRemoveFilter={handleRemoveFilter}
                    onClearAll={handleClearFilters}
                />
            </div>
            <div className={isTableFixed ? 'flex-1 overflow-hidden' : ''}>
                <DataTable
                columns={columns}
                columnVisibility={visibleColumns}
                data={tickets}
                enableGlobalFilter={false}
                enableColumnFilters={false}
                noDataMessage={
                    Object.keys(filters).length > 2 || searchTerm
                        ? "Δεν βρέθηκαν αποτελέσματα με τα επιλεγμένα φίλτρα"
                        : "Δεν υπάρχουν αιτήματα προς εμφάνιση"
                }
                onRowClick={handleRowClick}
                getRowLink={(row) => `/tickets/${row.id}`}
                isLoading={isLoading}
                serverPagination={data ? {
                    totalPages: data.total_pages,
                    totalResults: data.total_results,
                    onPageChange: handlePageChange
                } : undefined}
                initialState={{
                    pagination: {
                        pageIndex: (filters.page_number ?? 1) - 1,
                        pageSize: filters.page_size ?? 50,
                    }
                }}
                fixedHeight={isTableFixed}
            />
            </div>
        </div>
    );
};

export default TicketsList;